import { useForm } from '@tanstack/react-form';
import { Button } from '@sara/ui/components/button';
import { Input } from '@sara/ui/components/input';
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
} from '@sara/ui/components/field';
import { editInterviewTypeFormSchema, type EditInterviewTypeFormValues } from './schemas';

interface EditInterviewTypeFormProps {
  defaultValues: EditInterviewTypeFormValues;
  onSubmit: (values: EditInterviewTypeFormValues) => void;
  onCancel: () => void;
  isPending: boolean;
}

// Metadata-only dialog: name / slug / description. The agent's system prompt +
// first message are edited on the template editing page (alongside the rest of
// the config); this dialog never writes to the ElevenLabs agent.
export function EditInterviewTypeForm({
  defaultValues,
  onSubmit,
  onCancel,
  isPending,
}: EditInterviewTypeFormProps) {
  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: editInterviewTypeFormSchema,
    },
    onSubmit: ({ value }) => {
      onSubmit(value);
    },
  });

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        e.stopPropagation();
        void form.handleSubmit();
      }}
      className="space-y-4"
      autoComplete="off"
    >
      <form.Field name="name">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0;
          return (
            <Field data-invalid={hasError || undefined}>
              <FieldLabel htmlFor={field.name}>Name</FieldLabel>
              <FieldContent>
                <Input
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isPending}
                  aria-invalid={hasError}
                  autoComplete="off"
                />
                <FieldError errors={field.state.meta.errors} />
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      <form.Field name="slug">
        {(field) => {
          const hasError = field.state.meta.errors.length > 0;
          return (
            <Field data-invalid={hasError || undefined}>
              <FieldLabel htmlFor={field.name}>Slug</FieldLabel>
              <FieldContent>
                <Input
                  id={field.name}
                  value={field.state.value}
                  onBlur={field.handleBlur}
                  onChange={(e) => field.handleChange(e.target.value)}
                  disabled={isPending}
                  aria-invalid={hasError}
                  autoComplete="off"
                />
                <FieldDescription>
                  Shown in webhook payloads. Changing this may affect integrations.
                </FieldDescription>
                <FieldError errors={field.state.meta.errors} />
              </FieldContent>
            </Field>
          );
        }}
      </form.Field>

      <form.Field name="description">
        {(field) => (
          <Field>
            <FieldLabel htmlFor={field.name}>Description</FieldLabel>
            <FieldContent>
              <Input
                id={field.name}
                value={field.state.value}
                onBlur={field.handleBlur}
                onChange={(e) => field.handleChange(e.target.value)}
                disabled={isPending}
                autoComplete="off"
              />
            </FieldContent>
          </Field>
        )}
      </form.Field>

      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isPending}>
          Cancel
        </Button>
        <Button type="submit" disabled={isPending}>
          {isPending ? 'Saving…' : 'Save'}
        </Button>
      </div>
    </form>
  );
}
